import { StorageTypes } from '../enums';

export class StorageUtil {
    private static _localStorageAvailable: boolean | null = null;
    private static _cookiesAvailable: boolean | null = null;
    private static _sessionStorageAvailable: boolean | null = null;

    private static init(): void {
        if (this._sessionStorageAvailable !== null) {
            return; // Already initialized
        }

        try {
            const testKey = '__storage_test__';
            localStorage.setItem(testKey, 'test');
            localStorage.removeItem(testKey);
            this._localStorageAvailable = true;
        }
 catch (e) {
            this._localStorageAvailable = false;
        }

        try {
            const testKey = '__storage_test__';
            document.cookie = `${testKey}=test; path=/; max-age=1`;
            const cookieWorks = document.cookie.includes(`${testKey}=test`);
            document.cookie = `${testKey}=; path=/; max-age=0`;
            this._cookiesAvailable = cookieWorks;
        }
 catch (e) {
            this._cookiesAvailable = false;
        }

        try {
            const testKey = '__storage_test__';
            sessionStorage.setItem(testKey, 'test');
            sessionStorage.removeItem(testKey);
            this._sessionStorageAvailable = true;
        }
 catch (e) {
            this._sessionStorageAvailable = false;
        }
    }

    static isLocalStorageAvailable(): boolean {
        this.init();
        return this._localStorageAvailable;
    }

    static areCookiesAvailable(): boolean {
        this.init();
        return this._cookiesAvailable;
    }

    static isSessionStorageAvailable(): boolean {
        this.init();
        return this._sessionStorageAvailable;
    }

    static getItem(key: string, fallbackOrder: (StorageTypes.Cookies | StorageTypes.LocalStorage | StorageTypes.SessionStorage)[] = [StorageTypes.LocalStorage, StorageTypes.Cookies, StorageTypes.SessionStorage]): string | null {
        this.init();

        for (const storageType of fallbackOrder) {
            if (storageType === StorageTypes.LocalStorage && this._localStorageAvailable) {
                return localStorage.getItem(key);
            }
 else if (storageType === StorageTypes.Cookies && this._cookiesAvailable) {
                return this.getCookie(key);
            }
 else if (storageType === StorageTypes.SessionStorage && this._sessionStorageAvailable) {
                return sessionStorage.getItem(key);
            }
        }

        return null;
    }

    static setItem(key: string, value: string, fallbackOrder: (StorageTypes.Cookies | StorageTypes.LocalStorage | StorageTypes.SessionStorage)[] = [StorageTypes.LocalStorage, StorageTypes.Cookies, StorageTypes.SessionStorage]): void {
        this.init();

        for (const storageType of fallbackOrder) {
            if (storageType === StorageTypes.LocalStorage && this._localStorageAvailable) {
                localStorage.setItem(key, value);
                return;
            }
 else if (storageType === StorageTypes.Cookies && this._cookiesAvailable) {
                this.setCookie(key, value);
                return;
            }
 else if (storageType === StorageTypes.SessionStorage && this._sessionStorageAvailable) {
                sessionStorage.setItem(key, value);
                return;
            }
        }
    }

    static removeItem(key: string, fallbackOrder: (StorageTypes.Cookies | StorageTypes.LocalStorage | StorageTypes.SessionStorage)[] = [StorageTypes.LocalStorage, StorageTypes.Cookies, StorageTypes.SessionStorage]): void {
        this.init();

        for (const storageType of fallbackOrder) {
            if (storageType === StorageTypes.LocalStorage && this._localStorageAvailable) {
                localStorage.removeItem(key);
                return;
            }
 else if (storageType === StorageTypes.Cookies && this._cookiesAvailable) {
                this.deleteCookie(key);
                return;
            }
 else if (storageType === StorageTypes.SessionStorage && this._sessionStorageAvailable) {
                sessionStorage.removeItem(key);
                return;
            }
        }
    }

    static clear(storageType: StorageTypes.LocalStorage | StorageTypes.SessionStorage): void {
        this.init();

        if (storageType === StorageTypes.LocalStorage && this._localStorageAvailable) {
            localStorage.clear();
        }
 else if (storageType === StorageTypes.SessionStorage && this._sessionStorageAvailable) {
            sessionStorage.clear();
        }
    }

    private static getCookie(name: string): string | null {
        const matches = document.cookie.match(new RegExp(
            `(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`
        ));
        return matches ? decodeURIComponent(matches[1]) : null;
    }

    private static setCookie(name: string, value: string): void {
        const maxAge = 365 * 24 * 60 * 60; // 365 days in seconds
        const secure = location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Strict${secure}`;
    }

    private static deleteCookie(name: string): void {
        document.cookie = `${name}=; path=/; max-age=0`;
    }
}
